import type { GamePlayerProjectionTrading } from '@library/types/trading';
import type { RequestSimulationRunResponse } from '@library/types/simulations';
import { useCallback } from 'react';
import { useGetSimulationsQuery } from '@bo-services/simulationsApi';

export function useSimulations({
  gameId,
  leagueId
}: {
  gameId?: string;
  leagueId?: string;
}): {
  simulations?: Array<RequestSimulationRunResponse>;
  isSimulationsError: boolean;
  isSimulationsLoading: boolean;
  handleBulkChangeProjectionTime: (params: {
    projectionBoardTime?: string;
    projectionStartTime?: string;
    isTopLevelChange?: boolean;
    simsProjections: Array<GamePlayerProjectionTrading>;
    updateProjection: ({
      change,
      initialProjection
    }: {
      change: GamePlayerProjectionTrading;
      initialProjection: GamePlayerProjectionTrading;
    }) => void;
  }) => void;
} {
  const {
    data: simulationsData,
    isError: isSimulationsError,
    isFetching,
    isLoading
  } = useGetSimulationsQuery(
    {
      gameId: gameId ?? '',
      leagueId: leagueId ?? ''
    },
    { skip: !leagueId || !gameId }
  );

  const isSimulationsLoading = isLoading || isFetching;

  /* Adding a function to handle bulk time change for Sims projections */
  const handleBulkChangeProjectionTime = useCallback(
    ({
      projectionBoardTime,
      projectionStartTime,
      simsProjections,
      updateProjection
    }: {
      simsProjections: Array<GamePlayerProjectionTrading>;
      projectionBoardTime?: string;
      projectionStartTime?: string;
      updateProjection: ({
        change,
        initialProjection
      }: {
        change: GamePlayerProjectionTrading;
        initialProjection: GamePlayerProjectionTrading;
      }) => void;
    }) => {
      simsProjections.forEach((projection) => {
        if (projectionBoardTime || projectionStartTime) {
          updateProjection({
            change: {
              ...projection,
              ...(projectionStartTime && { startTime: projectionStartTime }),
              ...(projectionBoardTime && { boardTime: projectionBoardTime })
            },
            initialProjection: projection
          });
        }
      });
    },
    []
  );

  return {
    simulations: simulationsData?.data,
    isSimulationsError,
    isSimulationsLoading,
    handleBulkChangeProjectionTime
  };
}
